import { Link } from 'react-router-dom';
import { ArrowLeft } from '@phosphor-icons/react';

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen flex flex-col" style={{ fontFamily: "'Arial', sans-serif" }}>
      {/* Purple Header Bar */}
      <header className="bg-[#3D1D72] text-white py-4 px-6 shadow-md">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-90 transition-opacity">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <span className="text-xl font-bold">H</span>
            </div>
            <span className="text-xl font-semibold">HERChain Solutions</span>
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-white/80 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft size={18} />
            Back to Home
          </Link>
        </div>
      </header>

      {/* White Body Content */}
      <main className="flex-1 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <h1 className="text-3xl md:text-4xl font-bold text-[#3D1D72] mb-2">
            Terms of Service
          </h1>
          <p className="text-gray-500 mb-8">Effective Date: March 2026</p>
          
          <div className="prose prose-lg max-w-none space-y-8">
            {/* Section 1 */}
            <section>
              <h2 className="text-xl font-bold text-[#3D1D72] mb-3">1. ABOUT HERCHAIN SOLUTIONS</h2>
              <p className="text-gray-700 leading-relaxed">
                HERChain Solutions is a cloud-based procurement management platform built specifically for Caribbean hospitality properties. The platform is operated by HERChain Solutions (pending formal registration as HERChain Solutions LLC) and is accessible at herchainsolutions.com. By accessing or using the platform you agree to be bound by these Terms of Service.
              </p>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-xl font-bold text-[#3D1D72] mb-3">2. THE SERVICE</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                HERChain Solutions provides a cloud-based procurement management platform including:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 mb-4">
                <li>Purchase requisition management</li>
                <li>Purchase order creation and management</li>
                <li>Vendor portal</li>
                <li>Freight forwarder portal</li>
                <li>Financial controller portal</li>
                <li>Receiver portal</li>
                <li>Storekeeper portal</li>
                <li>Document vault</li>
                <li>Automatic notification and reminder system</li>
                <li>Permanent approval audit trail</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to add, modify, or discontinue features with reasonable notice to active subscribers.
              </p>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-xl font-bold text-[#3D1D72] mb-3">3. SUBSCRIPTION AND PAYMENT</h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>HERChain Solutions is offered on a monthly or annual subscription basis.</li>
                <li>Pricing is based on the selected plan tier and number of properties subscribed.</li>
                <li>A one-time onboarding fee applies to each property and is non-refundable once onboarding has commenced.</li>
                <li>Monthly subscriptions are billed in advance on the first day of each billing cycle.</li>
                <li>Annual subscriptions are billed in full at commencement.</li>
                <li>All fees are quoted and payable in United States Dollars.</li>
                <li>Payment is due within 14 days of invoice date.</li>
                <li>Late payment may result in suspension of platform access.</li>
                <li>We reserve the right to adjust pricing with a minimum of 60 days written notice to active subscribers.</li>
              </ul>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-xl font-bold text-[#3D1D72] mb-3">4. ACCOUNT RESPONSIBILITIES</h2>
              <p className="text-gray-700 leading-relaxed">
                You are responsible for maintaining the confidentiality of all login credentials for your property's user accounts. You are responsible for all activity that occurs under your property's accounts. You must notify us immediately at <a href="mailto:support@herchainsolutions.com" className="text-[#3D1D72] hover:underline">support@herchainsolutions.com</a> if you become aware of any unauthorised access to your accounts.
              </p>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-xl font-bold text-[#3D1D72] mb-3">5. ACCEPTABLE USE</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                You agree to use HERChain Solutions only for lawful procurement management purposes. You agree not to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Use the platform for any unlawful purpose</li>
                <li>Attempt to gain unauthorised access to any part of the platform</li>
                <li>Upload malicious code or harmful content</li>
                <li>Store or transmit fraudulent procurement records</li>
                <li>Attempt to reverse engineer or copy any part of the platform</li>
                <li>Share login credentials with unauthorised individuals</li>
              </ul>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-xl font-bold text-[#3D1D72] mb-3">6. DATA OWNERSHIP AND PRIVACY</h2>
              <p className="text-gray-700 leading-relaxed">
                All procurement data entered into HERChain Solutions by your property belongs entirely to your property. Our collection and use of your data is governed by our Privacy Policy available at <Link to="/privacy-policy" className="text-[#3D1D72] hover:underline">herchainsolutions.com/privacy-policy</Link>.
              </p>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="text-xl font-bold text-[#3D1D72] mb-3">7. PLATFORM AVAILABILITY</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We aim to maintain platform availability at all times and will make reasonable efforts to minimise downtime. We are not liable for temporary unavailability due to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Scheduled maintenance</li>
                <li>Emergency maintenance</li>
                <li>Third-party infrastructure issues</li>
                <li>Force majeure events</li>
              </ul>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="text-xl font-bold text-[#3D1D72] mb-3">8. INTELLECTUAL PROPERTY</h2>
              <p className="text-gray-700 leading-relaxed">
                HERChain Solutions including its platform design, workflow architecture, codebase, and branding is owned exclusively by HERChain Solutions. Your subscription grants you a limited non-exclusive non-transferable licence to access and use the platform for your property's internal procurement management purposes during the subscription period.
              </p>
            </section>

            {/* Section 9 */}
            <section>
              <h2 className="text-xl font-bold text-[#3D1D72] mb-3">9. LIMITATION OF LIABILITY</h2>
              <p className="text-gray-700 leading-relaxed">
                To the maximum extent permitted by applicable law HERChain Solutions shall not be liable for any indirect, incidental, or consequential loss arising from your use of the platform. Our total liability for any claim shall not exceed the total subscription fees paid by you in the three months preceding the claim.
              </p>
            </section>

            {/* Section 10 */}
            <section>
              <h2 className="text-xl font-bold text-[#3D1D72] mb-3">10. TERMINATION</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                You may terminate your subscription at any time by providing 30 days written notice to <a href="mailto:support@herchainsolutions.com" className="text-[#3D1D72] hover:underline">support@herchainsolutions.com</a>.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                We reserve the right to suspend or terminate your access with immediate effect if:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>You are in material breach of these Terms</li>
                <li>Payment is outstanding for more than 30 days</li>
                <li>Your use poses a security risk</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                Following termination your data will be retained for 30 days during which you may request a complete export.
              </p>
            </section>

            {/* Section 11 */}
            <section>
              <h2 className="text-xl font-bold text-[#3D1D72] mb-3">11. MODIFICATIONS TO THESE TERMS</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update these Terms of Service from time to time with a minimum of 30 days notice to active subscribers by email.
              </p>
            </section>

            {/* Section 12 */}
            <section>
              <h2 className="text-xl font-bold text-[#3D1D72] mb-3">12. CONTACT US</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                HERChain Solutions<br />
                <a href="mailto:support@herchainsolutions.com" className="text-[#3D1D72] hover:underline">support@herchainsolutions.com</a><br />
                <a href="https://herchainsolutions.com" className="text-[#3D1D72] hover:underline">herchainsolutions.com</a>
              </p>
              <p className="text-gray-500 text-sm italic">
                Note: HERChain Solutions is currently pending formal registration as HERChain Solutions LLC. These Terms will be updated upon completion of registration.
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#3D1D72] text-white py-8 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/50 text-sm">
              &copy; {new Date().getFullYear()} HERChain Solutions. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link 
                to="/privacy-policy" 
                className="text-white/60 hover:text-white text-sm transition-colors"
              >
                Privacy Policy
              </Link>
              <span className="text-white text-sm font-medium">Terms of Service</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfServicePage;
